import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faXmark, faClose } from '@fortawesome/free-solid-svg-icons'
import { useClickOutside } from '../../hooks/useClickOutside'
import AIRPNumberInput from '../../../components/common/AIRPNumberInput/AIRPNumberInput'
import Badge from '../../../components/common/Badge/Badge'
import Button from '../../../components/common/Button/Button'
import DatePicker from '../../../components/common/DatePicker/DatePicker'
import Dropdown from '../../../components/common/Dropdown/Dropdown'
import ToolTip from '../../../components/common/ToolTip/ToolTip'
import { ColumnType, FilterColumnInfo } from '../ColumnInfo'
import './GenericFilter.css'
import MultiselectDropdown from '../../../components/common/MultiselectDropdown/MultiselectDropdown'

export type Filter<T extends object = Record<string, unknown>> = {
    column: keyof T & string
    value: string | number | null | undefined
    display?: string
    method: FilterOption
    dataLkpFunc?: (value: T[keyof T]) => string | undefined
    filterFunc?: (object: T) => boolean
    displayValue?: string
}

type FilterOption = {
    text: string
    value: string
    input: "string" | "number" | "date" | "none" | "integer" | "dropdown" | JSX.Element
    func: (valA: unknown, valB?: unknown) => boolean
}

interface GenericFilterProps<T extends object> {
    columns: FilterColumnInfo[]
    filters: Filter<T>[]
    setFilters: React.Dispatch<React.SetStateAction<Filter<T>[]>>
}

export function filterData<T extends object>(data: T[], filters: Filter<T>[]) {
    return data.filter((element) => {
        return filters.every((filter) => {
            if (filter.filterFunc) {
                return filter.filterFunc(element)
            } else {
                const val = element[filter.column as keyof T]
                const updated = (filter.dataLkpFunc && filter.method.input !== "dropdown") ? filter.dataLkpFunc(val) : val
                return (filter.value === undefined || filter.value === null) ?
                    filter.method.func(updated)
                    :
                    filter.method.func(updated, filter.value)
            }
        })
    })
}

const contains = (valA: unknown, valB?: unknown) => String(valA ?? "").toLowerCase().includes(String(valB ?? "").toLowerCase())
const notContains = (valA: unknown, valB?: unknown) => !contains(valA, valB)
const equals = (valA: unknown, valB?: unknown) => String(valA ?? "").toLowerCase() === String(valB ?? "").toLowerCase()
const notEquals = (valA: unknown, valB?: unknown) => !equals(valA, valB)
const isBlank = (valA: unknown) => valA === null || valA === undefined || String(valA).trim() === ""
const isNotBlank = (valA: unknown) => !isBlank(valA)
const lessThan = (valA: unknown, valB?: unknown) => Number(valA) < Number(valB)
const greaterThan = (valA: unknown, valB?: unknown) => Number(valA) > Number(valB)
const numberEquals = (valA: unknown, valB?: unknown) => Number(valA) === Number(valB)
const numberNotEquals = (valA: unknown, valB?: unknown) => Number(valA) !== Number(valB)
const isNull = (valA: unknown) => valA === null || valA === undefined
const isNotNull = (valA: unknown) => !isNull(valA)
const before = (valA: unknown, valB?: unknown) => {
    const a = new Date(valA as string | Date).getTime()
    const b = new Date(valB as string | Date).getTime()
    return a < b
}
const after = (valA: unknown, valB?: unknown) => {
    const a = new Date(valA as string | Date).getTime()
    const b = new Date(valB as string | Date).getTime()
    return a > b
}
const isTrue = (valA: unknown) => valA === true
const isFalse = (valA: unknown) => valA === false
const arrayIncludes = (valA: unknown, valB?: unknown) => Array.isArray(valB) ? valB.includes(String(valA)) : false
export const filterOptions: Record<string, FilterOption[]> = {
    "string": [
        { text: "Contains", value: "Contains", input: "string", func: contains },
        { text: "Not Contains", value: "NotContains", input: "string", func: notContains },
        { text: "Equals", value: "Equals", input: "string", func: equals },
        { text: "Not Equals", value: "NotEquals", input: "string", func: notEquals },
        { text: "Is Blank", value: "IsBlank", input: "none", func: isBlank },
        { text: "Is Not Blank", value: "IsNotBlank", input: "none", func: isNotBlank },
    ],
    "date": [
        { text: "Before", value: "Before", input: "date", func: before },
        { text: "After", value: "After", input: "date", func: after },
        { text: "Equals", value: "Equals", input: "date", func: equals },
        { text: "Not Equals", value: "NotEquals", input: "date", func: notEquals },
        { text: "Is Blank", value: "IsBlank", input: "none", func: isBlank },
        { text: "Is Not Blank", value: "IsNotBlank", input: "none", func: isNotBlank },
    ],
    "number": [
        { text: "=", value: "Equals", input: "number", func: numberEquals },
        { text: "!=", value: "NotEquals", input: "number", func: numberNotEquals },
        { text: "<", value: "<", input: "number", func: lessThan },
        { text: ">", value: ">", input: "number", func: greaterThan },
        { text: "Is Null", value: "IsNull", input: "none", func: isNull },
        { text: "Is Not Null", value: "IsNotNull", input: "none", func: isNotNull },
    ],
    "integer": [
        { text: "=", value: "Equals", input: "integer", func: numberEquals },
        { text: "!=", value: "NotEquals", input: "integer", func: numberNotEquals },
        { text: "<", value: "<", input: "integer", func: lessThan },
        { text: ">", value: ">", input: "integer", func: greaterThan },
        { text: "Is Null", value: "IsNull", input: "none", func: isNull },
        { text: "Is Not Null", value: "IsNotNull", input: "none", func: isNotNull },
    ],
    "boolean": [
        { text: "Is True", value: "IsTrue", input: "none", func: isTrue },
        { text: "Is False", value: "IsFalse", input: "none", func: isFalse },
        { text: "Is Null", value: "IsNull", input: "none", func: isNull },
        { text: "Is Not Null", value: "IsNotNull", input: "none", func: isNotNull },
    ],
    "dropdown": [
        { text: "=", value: "Equals", input: "dropdown", func: arrayIncludes }
    ]
}

function GenericFilter<T extends object>(props: GenericFilterProps<T>) {
    const { columns, filters, setFilters } = props
    const ref = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation(["lease_table", "common"])

    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [currentColumn, setCurrentColumn] = useState<FilterColumnInfo>()
    const [currentFilterOption, setCurrentFilterOption] = useState<FilterOption>()
    const [filterValue, setFilterValue] = useState<null | string | number | string[]>(null)

    const clearForm = () => {
        setCurrentColumn(undefined)
        setCurrentFilterOption(undefined)
        setFilterValue(null)
    }

    const getFilterOptionsForColumn = (column: FilterColumnInfo | undefined): FilterOption[] => {
        if (!column) return []

        const type: ColumnType = column.type || "string"
        return filterOptions[column.filterDropdown ? "dropdown" : type]
    }

    const chooseColumn = (column: FilterColumnInfo) => {
        setCurrentColumn(column)
        setCurrentFilterOption(getFilterOptionsForColumn(column)[0])
        setFilterValue(null)
    }

    const getColumnDisplay = (column: FilterColumnInfo): string => {
        if (column.filterDisplay) {
            return column.filterDisplay
        }
        if (column.display && typeof column.display === "string") {
            return column.display
        }
        if (column.id) {
            return column.id
        }
        return ""
    }

    const getFilterOptionByValue = (value: string): FilterOption | undefined => {
        return getFilterOptionsForColumn(currentColumn).find((option) => option.value === value)
    }

    const handleFilterMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentFilterOption(getFilterOptionByValue(e.target.value))
    }

    const handleFilterInputChange = (e: React.ChangeEvent<HTMLInputElement> | number | null | Date | string[] | undefined) => {
        // null is considered an object for some reason (thanks js, try: typeof null)
        if (e === null || e === undefined) {
            setFilterValue(null)
        }
        else if (typeof e === "object" && "target" in e) {
            const casted = e as React.ChangeEvent<HTMLInputElement>
            setFilterValue(casted.target.value)
        } else if (e instanceof Date) {
            setFilterValue(format(e, "yyyy-MM-dd"))
        } else {
            setFilterValue(e)
        }
    }

    const getFilterInput = (column: FilterColumnInfo | undefined) => {
        switch (currentFilterOption?.input) {
            case "string":
                return <input value={filterValue as string}
                    onChange={handleFilterInputChange}
                    type="text"
                    autoComplete='off'></input>
                break
            case "number":
                return <AIRPNumberInput formatType="decimal" readOnly={false}
                    initialValue={filterValue as number | null}
                    className='border-hover limited-input'
                    onChange={(value: number | null) => handleFilterInputChange(value)} />
            case "integer":
                return <AIRPNumberInput formatType="integer"
                    readOnly={false}
                    initialValue={filterValue as number | null}
                    className='border-hover limited-input'
                    onChange={(value: number | null) => handleFilterInputChange(value)} />
            case "date":
                return <div id="filter-date-input-container" className='filter-date-input-container'>
                    <DatePicker readOnly={false} disabled={false}
                        id="filter-value-input"
                        name="filter-value-input"
                        position="top-start"
                        date={filterValue as string}
                        onChange={(value) => handleFilterInputChange(value)}
                    />
                </div>
            case "dropdown":
                return <div id="filter-dropdown-container" className='filter-dropdown-container'>
                    <MultiselectDropdown name="filter-dropdown"
                      id="filter-dropdown"
                      categoryName={column ? (typeof(column.display) === "string" ? column.display : (column.filterDisplay ? column.filterDisplay :  "filter option")) : "filter option"}
                      options={column?.filterDropdown ?? []}
                      selectedOptions={filterValue === null ? [] : (filterValue as string[])}
                      setSelectedOptions={(selectedOptions) => handleFilterInputChange(selectedOptions)}
                      preventDefault={true}
                      enabledInReadonly={true} 
                      placeholder=""/>
                    </div>
            default:
                return null
        }
    }

    const removeFilter = (key: number) => {
        const updated = filters.filter((_, i) => i !== key)
        setFilters(updated)
        setIsPopoverOpen(false)
    }

    const addFilter = () => {
        if (!currentColumn || !currentFilterOption
            || (currentFilterOption.input !== "none" && (filterValue === undefined || filterValue === null || (Array.isArray(filterValue) && filterValue.length === 0))))
            return

        setFilters([
            ...filters,
            {
                column: currentColumn.id as keyof T & string,
                display: getColumnDisplay(currentColumn),
                value: filterValue,
                method: currentFilterOption,
                dataLkpFunc: currentColumn.dataLkpFunc, //as ((value: T[keyof T]) => string | undefined) | undefined
                displayValue: currentColumn.filterDropdown ? (Array.isArray(filterValue) && filterValue.length > 0 ? 
                    filterValue.reduce((displayed, value) => displayed + ", " + currentColumn.filterDropdown?.find((option) => option.value === value)?.text, "").substring(2)
                    : undefined) : undefined
            } as Filter<T>
        ])
    }

    useEffect(() => {
        const onDatePickerMutationCallback: MutationCallback = () => {
            if (contentRef.current) {
                const filterDateInputContainer = contentRef.current.querySelector(".filter-date-input-container") as HTMLDivElement
                if (filterDateInputContainer) {
                    const dataPickerPopper = contentRef.current.querySelector(".react-datepicker-popper")
                    if (dataPickerPopper) {
                        filterDateInputContainer.style.minHeight = (dataPickerPopper.clientHeight) + "px"
                        filterDateInputContainer.scrollIntoView({
                            behavior: 'smooth',
                            block: "end"
                        }
                        )
                    } else {
                        filterDateInputContainer.style.minHeight = ""
                    }
                }
            }
        }

        if (currentFilterOption && currentFilterOption.input === "date" && contentRef.current) {
            const datePickerContainer = contentRef.current.querySelector(".datepicker-container")
            if (datePickerContainer) {
                const observer = new MutationObserver(onDatePickerMutationCallback)
                observer.observe(datePickerContainer, { childList: true, subtree: true })
                return () => observer.disconnect()
            }
        }
        return
    }, [currentFilterOption])

    useClickOutside(ref, (event?: MouseEvent) => {
        if (event) {
            const target = event.target as HTMLElement
            if (target && target.className && (typeof target.className === "string")
                && target.className.includes("datepicker")) {
                return
            }
        }
        setIsPopoverOpen(false)
    })

    return (
        <div ref={ref} className="filter-popover">
            <div className='flex flex-row'>
                <ToolTip contents={
                    <Button
                        onClick={() => {
                            clearForm()
                            setIsPopoverOpen(!isPopoverOpen)
                        }}
                        text={<FontAwesomeIcon icon={faFilter} />}
                        ariaLabel="filter"
                        forceEnabled={true} />
                }>
                    {t('tooltips.filter-tooltip')}
                </ToolTip>
                <Badge badgeStyle="info" badge={
                    filters.length > 0 ?
                        <>{filters.length}</>
                        :
                        undefined
                }
                />
            </div>
            {isPopoverOpen &&
                <div ref={contentRef} className="popover-content">
                    <div className="width-100 flex flex-row justify-content-end">
                        <FontAwesomeIcon className='close-icon'
                            icon={faClose}
                            onClick={() => setIsPopoverOpen(false)} />
                    </div>
                    <form className='width-100 flex flex-column'
                        onChange={(e) => e.stopPropagation()} onFocus={(e) => e.stopPropagation()}
                        onSubmit={(e) => {
                            e.preventDefault()
                            addFilter()
                            setIsPopoverOpen(false)
                        }}>
                        <div className='flex flex-column'>
                            <div>{t("filter.choose-column")}</div>
                            <div className="filter-popper-column">
                                <select name="filterColumn"
                                    className='width-fit-content'
                                    value={currentColumn ? currentColumn.id : ""}
                                    onChange={(e) => {
                                        const selectedIndex = ((e.target) as HTMLSelectElement).selectedIndex
                                        chooseColumn(columns[selectedIndex - 1])
                                    }}>
                                    <option value="" disabled></option>
                                    {columns.map((col) => (
                                        <option key={col.id} value={col.id}>
                                            {getColumnDisplay(col)}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        {
                            (currentColumn && !currentColumn.filterDropdown) &&
                            <div className='flex flex-column'>
                                <div>Filter method:</div>
                                <div className="filter-popper-column">
                                    <Dropdown onChange={handleFilterMethodChange}
                                        name="filterMethod"
                                        options={getFilterOptionsForColumn(currentColumn)}
                                        value={currentFilterOption?.value}
                                        forceEnabled={true} />
                                </div>
                            </div>
                        }
                        {(currentFilterOption && currentFilterOption.input !== "none") &&
                            <div className='flex flex-column'>
                                <div>Value to filter by:</div>
                                <div className='filter-input-container'>
                                    {getFilterInput(currentColumn)}
                                </div>
                            </div>
                        }

                        <div className='width-100'>
                            <button type="submit">{t("filter.add-filter")}</button>
                        </div>
                        {
                            filters.length > 0 &&
                            <>
                                <hr />
                                <div>
                                    <table className="filter-table">
                                        <tbody>
                                            {
                                                filters.map((filter, key) => (
                                                    <tr key={key}>
                                                        <td key={`filter-search-row-${key}-delete-icon`}>
                                                            <FontAwesomeIcon icon={faXmark}
                                                                onClick={() => removeFilter(key)} />
                                                        </td>
                                                        <td key={`filter-search-row-${key}-column-name`}
                                                            className="no-wrap">
                                                            {filter.display}
                                                        </td>
                                                        <td key={`filter-search-row-${key}-filter-rule`}
                                                            className="no-wrap">
                                                            {filter.method.text}
                                                        </td>
                                                        <td key={`filter-search-row-${key}-filter-text`}
                                                            className={filter.displayValue ? "" : "no-wrap"}>
                                                            {filter.displayValue ?? filter.value}
                                                        </td>
                                                    </tr>
                                                )
                                                )
                                            }
                                        </tbody>
                                    </table>
                                </div>
                                <div className='button-group'>
                                    <button type="button" onClick={() => {
                                        setFilters([])
                                        setIsPopoverOpen(false)
                                    }
                                    }>
                                        <div className='no-wrap'>Remove All</div>
                                    </button>
                                    <button type="button" onClick={() => {
                                        setFilters([...filters])
                                        setIsPopoverOpen(false)
                                    }
                                    }>
                                        Refilter
                                    </button>
                                </div>
                            </>
                        }
                    </form>
                </div >
            }
        </div>
    )
}

export default GenericFilter